// app/routes/home.tsx
import {
  HeadersFunction,
  json,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from '@remix-run/node';
import { useLoaderData, useNavigate, useSearchParams } from '@remix-run/react';
import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { createRef, useEffect, useRef, useState } from 'react';
import { FaCircleCheck } from 'react-icons/fa6';
import { IoArrowUpOutline } from 'react-icons/io5';
import Footer from '~/components/Footer';
import { Header } from '~/components/Header';
import { usePostSignupRedirect } from '~/hooks/usePostSignupRedirect';
import { pricing, useCases, useCasesData } from '~/constants';
import '~/styles/home.css';
import '~/styles/homeIteration.css';
import '~/styles/pricing.css';
import { EnquireForm } from '../components/EnquireForm';
import LoadingButton from '~/components/LoadingButton';
import { auth } from '~/utils/auth/firebase-service';
import { loginWithFirebase } from '~/utils/ServerRequest';
import UseCaseModal, { UseCase } from '~/components/UseCaseModal';

import HeroSection from '~/components/HomeComponent/HeroSection';
import FeaturesSection from '~/components/HomeComponent/FeatureSection';
import InsightsSection from '~/components/HomeComponent/InsightSection';
import UseCaseSection from '~/components/HomeComponent/UseCaseSection';
import { useMounted } from '~/hooks/useMounted';

export const headers: HeadersFunction = () => ({});

export const meta: MetaFunction = () => {
  return [
    { title: 'Home - Expressed Behaviour Analytics Platform' },
    {
      name: 'description',
      content: 'Welcome to the Expressed Behaviour Analytics Platform!',
    },
  ];
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  return json('', {
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function action(/*request*/) {
  return redirect('/home/');
}

export default function Home() {
  const resp = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [enquire, setEnquire] = useState<boolean>(false);
  const [activeUseCase, setActiveUseCase] = useState<
    'digital' | 'customerExp' | 'consumer'
  >('digital');
  const [billingType, setBillingType] = useState('yearly');
  const [loadingGetStarted, setLoadingGetStarted] = useState(false);
  const [loadingEnquire, setLoadingEnquire] = useState(false);
  const [showUseCaseModal, setShowUseCaseModal] = useState<boolean>(false);
  const [selectedUseCase, setSelectedUseCase] = useState<UseCase | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [autoShowLogin, setAutoShowLogin] = useState(false);

  const pricingRef = createRef<HTMLDivElement>();
  const topRef = createRef<HTMLDivElement>();

  useEffect(() => {
    const acceptCode = searchParams.get('accept');
    const redirectParam = searchParams.get('redirect');
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Only call backend login if cookie is missing
        if (!window?.document?.cookie?.includes('auth_token')) {
          let loginSuccess = false;
          const result = await loginWithFirebase();
          if (result && typeof result === 'object' && !result.error) {
            loginSuccess = true;
          } else {
            setLoginError(result?.message || 'Login failed. Please try again.');
          }
          // Only navigate if login was successful
          if (loginSuccess) {
            if (redirectParam) {
              navigate(redirectParam);
            } else if (acceptCode) {
              navigate(
                `/projects/accept?code=${encodeURIComponent(acceptCode)}`
              );
            }
            // If you have other navigation logic (e.g. projects/list), ensure it's only inside this block
          }
        }
      } else {
        // If not logged in and redirect is present, auto-show login modal
        if (redirectParam) {
          setAutoShowLogin(true);
        } else if (acceptCode) {
          setAutoShowLogin(true);
        }
      }
    });
    return () => unsubscribe();
  }, [searchParams, navigate]);

  useEffect(() => {
    document.body.style.overflow = enquire ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [enquire]);

  const toggleEnquire = () => {
    setEnquire(!enquire);
  };

  const enquireFormButtonActions = [
    {
      label: 'Send',
      onClick: () => {
        toggleEnquire(); // Close modal on confirm
      },
    },
  ];

  // Makes the pricing columns have equal height
  useEffect(() => {
    const setEqualHeight = () => {
      const pricingColumns = document.querySelectorAll('.pricingColumn');
      let maxHeight = 0;

      pricingColumns.forEach((col) => {
        (col as HTMLElement).style.height = 'auto'; // Reset height before measuring
        maxHeight = Math.max(maxHeight, (col as HTMLElement).offsetHeight);
      });

      pricingColumns.forEach((col) => {
        (col as HTMLElement).style.height = `${maxHeight}px`;
      });
    };

    setEqualHeight(); // Set height on initial load
    window.addEventListener('resize', setEqualHeight); // Adjust on window resize

    return () => window.removeEventListener('resize', setEqualHeight);
  }, []);

  function triggerSignup(): void {
    const signupButton = document.getElementById('signupButton');
    if (signupButton !== null) {
      signupButton?.click();
    }
  }

  // Auto-show login modal if needed
  useEffect(() => {
    if (autoShowLogin) {
      const loginButton = document.getElementById('loginButton');
      if (loginButton) {
        loginButton.click();
        setAutoShowLogin(false);
      }
    }
  }, [autoShowLogin]);

  const openUseCaseModal = (useCaseId: number) => {
    const foundUseCase = useCasesData.find((uc) => uc.id === useCaseId);
    if (foundUseCase) {
      setSelectedUseCase(foundUseCase);
      setShowUseCaseModal(true);
    }
  };

  const closeUseCaseModal = () => {
    setShowUseCaseModal(false);
    setSelectedUseCase(null);
  };

  const PricingSection = () => {
    return (
      <div ref={pricingRef} id="pricingSection">
        <div className="toggle-container">
          <LoadingButton
            className={`toggle-button boldText ${billingType === 'monthly' ? 'active' : ''}`}
            onClick={async () => {
              setBillingType('monthly');
            }}
            loading={false}
          >
            Monthly
          </LoadingButton>
          <LoadingButton
            className={`toggle-button boldText ${billingType === 'yearly' ? 'active' : ''}`}
            onClick={async () => {
              setBillingType('yearly');
            }}
            loading={false}
          >
            Yearly
          </LoadingButton>
        </div>
        <div className="rowArrangement" id="pricingRow">
          {pricing.map((e, i) => {
            return (
              <div
                className={`pricingColumn ${e.title == 'Enterprise' && 'enterprise'}`}
                key={`pricing-${e.title}`}
                style={{
                  backgroundColor: `${e.title == 'Trial' ? '#0B99E7' : e.title == 'Basic' ? 'var(--secondary-shade-2)' : e.title == 'Professional' ? 'var(--secondary-shade-1)' : 'var(--accent)'}`,
                }}
              >
                <div
                  className={`pricingHeader ${e.title == 'Enterprise' && 'enterprise'}`}
                  style={{
                    backgroundColor: `${e.title == 'Trial' ? 'var(--secondary-shade-2)' : e.title == 'Basic' ? 'var(--secondary-shade-1)' : e.title == 'Professional' ? 'var(--secondary)' : 'var(--accent)'}`,
                  }}
                >
                  <span className="pricingTitle boldText">{e.title}</span>
                  <div className="price boldText">
                    {((billingType == 'yearly' && e.priceYearly) ||
                      (billingType == 'monthly' && e.priceMonthly)) && (
                      <span>
                        {billingType == 'yearly'
                          ? e.priceYearly
                          : e.priceMonthly}
                        {(e.title == 'Individual' ||
                          e.title == 'Professional') && (
                          <>
                            <span className="subscript">/month</span>

                            {billingType === 'yearly' &&
                            e.yearlyValue != null ? (
                              <div className="subprice">
                                (£{e.yearlyValue * 12}/year)
                              </div>
                            ) : (
                              ''
                            )}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className={'featureColumn'}>
                  <table className="featureTable boldText">
                    <tbody>
                      {e.feature.map((feature, i) => (
                        <tr
                          key={`${e.title}-${feature}`}
                          className={
                            i === e.feature.length - 1 ? 'noBottomBorder' : ''
                          }
                        >
                          <td className="featureCheckColumn">
                            <FaCircleCheck
                              className="featureCheck"
                              style={{
                                color:
                                  e.title == 'Enterprise'
                                    ? 'var(--tertiary)'
                                    : undefined,
                              }}
                            />
                          </td>
                          <td className="featureTextColumn">
                            <span>{feature}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <LoadingButton
                  className={'pricingButton'}
                  onClick={async () => {
                    if (e.title == 'Trial') {
                      setLoadingGetStarted(true);
                      const signupButton =
                        document.getElementById('signupButton');
                      signupButton?.click();
                      setLoadingGetStarted(false);
                    } else {
                      setLoadingGetStarted(true);
                      await new Promise((res) => setTimeout(res, 400));
                      // Only redirect if user is authenticated and login is successful
                      if (window?.document?.cookie?.includes('auth_token')) {
                        window.location.href =
                          e.link + '&billing=' + billingType;
                      }
                      setLoadingGetStarted(false);
                    }
                  }}
                  loading={loadingGetStarted}
                >
                  {e.cta}
                </LoadingButton>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const priorityPage: any = searchParams.get('page');
  const mounted = useMounted();
  return (
    <div>
      <Header pricingRef={pricingRef} priorityPage={priorityPage} />
      {/* <BackgroundGradient /> */}
      <div id="home" ref={topRef}>
        <HeroSection
          setLoadingGetStarted={setLoadingGetStarted}
          triggerSignup={triggerSignup}
          setLoadingEnquire={setLoadingEnquire}
          toggleEnquire={toggleEnquire}
        />
        {enquire && (
          <EnquireForm
            buttons={enquireFormButtonActions}
            onClose={toggleEnquire}
          />
        )}
        <FeaturesSection />
        <InsightsSection />
        <UseCaseSection openUseCaseModal={openUseCaseModal} />
        {/* <PricingSection /> */}
        {showUseCaseModal && selectedUseCase && (
          <UseCaseModal useCase={selectedUseCase} onClose={closeUseCaseModal} />
        )}
        {loginError && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.45)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '32px 40px',
                boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                minWidth: 320,
                maxWidth: 420,
                textAlign: 'center',
              }}
            >
              <h2 style={{ color: '#b00', marginBottom: 16 }}>Login Error</h2>
              <p style={{ marginBottom: 24 }}>{loginError}</p>
              <button
                style={{
                  padding: '8px 24px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#b00',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                }}
                onClick={() => setLoginError(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        <div className="missionSection">
          <p
            className="boldText"
            style={{ fontSize: '18px', color: 'var(--primary)' }}
          >
            BLUESKEYE AI are global leaders in using face data to understand
            expressed human behaviour. We have validated that our technology is
            better than the human eye for assessing changes in emotional
            responses.
          </p>
          <p style={{ fontSize: '18px' }}>
            In addition to commercial customers in the consumer, pharmaceutical
            and automotive spaces our technology is progressing through clinical
            trials to assure the UK health regulatory authorities of its
            efficacy.
          </p>
          <p style={{ fontSize: '18px' }}>
            BLUESKEYE AI uses continuous multidimensional approach to
            continuously measure people's emotion throughout their experience of
            using your product or consuming your content. This means we can
            measure transitions between emotional state throughout. This better
            fits with the real human experience and provides richer and more
            nuanced insights.
          </p>
          <p style={{ fontSize: '18px' }}>
            The technology has been in development for over 20 years, initially
            at the University of Nottingham and with Blueskeye since it was spun
            out of the University in 2019.
          </p>
          <p style={{ fontSize: '18px' }}>
            The technology has been trained using our data bank of over XX
            billion data items and been proven to work effectively irrespective
            of apparent age, gender and ethnicity.
          </p>
        </div>
        <LoadingButton
          id="topOfPageButton"
          onClick={async () => {
            await new Promise((res) => setTimeout(res, 200));
            topRef?.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          loading={false}
        >
          {`Return to top`}
          {<IoArrowUpOutline style={{ fontSize: '1.5vw' }} />}
        </LoadingButton>
      </div>
      <div className="footerSection">{mounted && <Footer />}</div>
    </div>
  );
}
